import { Label } from '~/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';

/**
 * What each kind of year asks of the family once the school enrolls with the
 * checklist. Both ways of adding a family to a year show these.
 */
export const CHECKLIST_KIND_WORDS = {
  RE_ENROLLMENT: {
    description: 'For families already at your school',
    steps: ["They say who's coming back", 'Then they finish the re-enrollment checklist'],
  },
  NEW_ADMISSION: {
    description: 'For new families joining your school',
    steps: ['They accept your offer for each child you set to Coming', 'Then they finish the admission checklist'],
  },
};

interface SelectKindStepProps {
  value: string;
  onChange: (value: string) => void;
  checklistEnrollmentOn?: boolean;
}

function SelectKindStep({ value, onChange, checklistEnrollmentOn = false }: SelectKindStepProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select whether these enrollments are for new families applying to the school,
        or returning families re-enrolling for the upcoming year.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <Card
          className={`cursor-pointer transition-colors ${
            value === 'RE_ENROLLMENT'
              ? 'border-primary bg-primary/5'
              : 'hover:border-muted-foreground'
          }`}
          onClick={() => onChange('RE_ENROLLMENT')}
        >
          <CardHeader>
            <CardTitle className="text-lg">Re-Enrollment</CardTitle>
            <CardDescription>
              {checklistEnrollmentOn
                ? CHECKLIST_KIND_WORDS.RE_ENROLLMENT.description
                : 'For returning families who are already enrolled at the school'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-muted-foreground list-disc list-inside">
              {checklistEnrollmentOn ? (
                CHECKLIST_KIND_WORDS.RE_ENROLLMENT.steps.map((step) => <li key={step}>{step}</li>)
              ) : (
                <>
                  <li>Send re-enrollment invitations</li>
                  <li>Families confirm their intent to return</li>
                  <li>Streamlined confirmation process</li>
                </>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-colors ${
            value === 'NEW_ADMISSION'
              ? 'border-primary bg-primary/5'
              : 'hover:border-muted-foreground'
          }`}
          onClick={() => onChange('NEW_ADMISSION')}
        >
          <CardHeader>
            <CardTitle className="text-lg">New Admission</CardTitle>
            <CardDescription>
              {checklistEnrollmentOn
                ? CHECKLIST_KIND_WORDS.NEW_ADMISSION.description
                : 'For new families applying to join the school'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-muted-foreground list-disc list-inside">
              {checklistEnrollmentOn ? (
                CHECKLIST_KIND_WORDS.NEW_ADMISSION.steps.map((step) => <li key={step}>{step}</li>)
              ) : (
                <>
                  <li>Full application process</li>
                  <li>Admissions review required</li>
                </>
              )}
              <li>Inquiries can be marked for an interview</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 p-3 bg-muted rounded-md">
        <Label className="text-sm font-medium">Selected:</Label>
        <span className="ml-2 text-sm">
          {value === 'NEW_ADMISSION' ? 'New Admission' : 'Re-Enrollment'}
        </span>
      </div>
    </div>
  );
}

export default SelectKindStep;
